const sessions = new Map();
const MAX_HISTORY_ITEMS = 20;

const nowMs = () => {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
};

const toFixedMs = (value) => Number(value.toFixed(1));

const isDebugEnabled = () => {
  if (process.env.REACT_APP_CHART_PERF_DEBUG === "1") return true;

  try {
    return String(window.localStorage?.getItem?.("ajna_chart_perf_debug") || "").trim() === "1";
  } catch (_) {
    return false;
  }
};

const pushHistory = (summary) => {
  if (typeof window === "undefined") return;

  try {
    const history = Array.isArray(window.__ajnaChartPerfHistory)
      ? window.__ajnaChartPerfHistory
      : [];

    history.unshift(summary);
    window.__ajnaChartPerfHistory = history.slice(0, MAX_HISTORY_ITEMS);
    window.__ajnaLastChartPerf = summary;
  } catch (_) {}
};

const duration = (marks, from, to) => {
  const start = marks[from];
  const end = marks[to];
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (end < start) return null;
  return toFixedMs(end - start);
};

const buildSummary = (session) => {
  const marks = session.marks || {};

  const stages = [
    { key: "click_to_modal", ms: duration(marks, "click", "modal_open") },
    { key: "flag_fetch", ms: duration(marks, "flag_fetch_start", "flag_fetch_end") },
    { key: "scripts_load", ms: duration(marks, "scripts_load_start", "scripts_load_end") },
    { key: "payload_fetch", ms: duration(marks, "payload_fetch_start", "payload_fetch_end") },
    { key: "widget_bootstrap_to_ready", ms: duration(marks, "widget_init_start", "chart_ready") },
    { key: "apply_payload", ms: duration(marks, "apply_payload_start", "apply_payload_end") },
    { key: "iframe_fetch", ms: duration(marks, "iframe_fetch_start", "iframe_fetch_end") },
    { key: "iframe_load", ms: duration(marks, "iframe_fetch_end", "iframe_loaded") },
  ].filter((item) => Number.isFinite(item.ms));

  const totalMs =
    duration(marks, "click", "done") ||
    duration(marks, "click", "iframe_loaded") ||
    duration(marks, "click", "apply_payload_end");

  const slowestStage = stages.reduce(
    (max, current) => (current.ms > max.ms ? current : max),
    { key: "unknown", ms: -1 }
  );

  return {
    id: session.id,
    startedAt: session.startedAt,
    finishedAt: Date.now(),
    totalMs,
    path: session.meta?.path || "unknown",
    status: session.meta?.status || "ok",
    meta: session.meta || {},
    marks,
    stages,
    slowestStage: slowestStage.ms >= 0 ? slowestStage : null,
  };
};

export const startChartPerfSession = (meta = {}) => {
  const id = `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  const mark = nowMs();

  sessions.set(id, {
    id,
    startedAt: Date.now(),
    marks: { click: mark },
    meta: { ...meta },
  });

  return id;
};

export const markChartPerf = (sessionId, markName, metaPatch = null) => {
  if (!sessionId || !markName) return;

  const session = sessions.get(sessionId);
  if (!session) return;

  if (!Object.prototype.hasOwnProperty.call(session.marks, markName)) {
    session.marks[markName] = nowMs();
  }

  if (metaPatch && typeof metaPatch === "object") {
    session.meta = { ...session.meta, ...metaPatch };
  }
};

export const finishChartPerfSession = (sessionId, metaPatch = null) => {
  if (!sessionId) return null;

  const session = sessions.get(sessionId);
  if (!session) return null;

  markChartPerf(sessionId, "done", metaPatch);
  const summary = buildSummary(session);
  pushHistory(summary);

  if (isDebugEnabled()) {
    if (summary.slowestStage) {
      console.info(
        "[chart-perf] slowest stage",
        summary.slowestStage.key,
        `${summary.slowestStage.ms}ms`,
        summary
      );
    } else {
      console.info("[chart-perf] summary", summary);
    }
  }

  sessions.delete(sessionId);
  return summary;
};

export const dropChartPerfSession = (sessionId) => {
  if (!sessionId) return;
  sessions.delete(sessionId);
};
